/**
 * ForgeRuntimeStatus — typed enum for CMMD runtime lifecycle states.
 *
 * CMMD returns opaque `status` and `readinessPhase` strings.
 * This module maps those raw strings to the canonical Forge-side enum,
 * and provides helpers for display and filtering.
 */

// ── Enum ──────────────────────────────────────────────────────────────────────

export type ForgeRuntimeStatus =
  /**
   * REGISTERED BUT NOT PROVISIONED. The user has this project on their Forge
   * Cloud surface and no sprite has ever been created for it. This is the
   * normal state between importing a project and opening its first thread.
   *
   * It is deliberately NOT `stopped` (which claims a machine exists and is
   * hibernated), NOT `error` (nothing failed), and NOT `destroyed` (nothing was
   * torn down). Borrowing any of those would have the UI lie about
   * infrastructure that does not exist.
   */
  | "registered"
  | "running"
  | "provisioning"
  | "starting"
  /**
   * MEMORY-FROZEN AND RESUMABLE IN UNDER A SECOND. A Sprite suspends itself
   * ~1 s after its last inbound request, so this is the RESTING STATE of every
   * healthy cloud runtime, not an exceptional one.
   *
   * Deliberately distinct from `stopped`. They look alike and cost nothing
   * alike: `stopped` claims a machine that must be genuinely started, while a
   * suspended sprite resumes on the next inbound request — measured live at
   * 0.646 s on 2026-08-04. Collapsing the two (which this file used to do) made
   * the sub-second case behave like the expensive one, so opening a thread never
   * auto-connected and the surface read as stale.
   *
   * This is the same three-states-onto-two collapse that CMMD #3373 fixed in the
   * egress authorizers, one layer up.
   */
  | "suspended"
  | "stopped"
  | "draining"
  | "error"
  | "destroyed";

/**
 * The status of a cloud project row that has no runtime at all.
 *
 * Callers must use this rather than inventing a placeholder status, so the
 * "no runtime yet" case stays a single, greppable concept.
 */
export const UNPROVISIONED_CLOUD_PROJECT_STATUS: ForgeRuntimeStatus = "registered";

// ── Mapping ───────────────────────────────────────────────────────────────────

/**
 * Map CMMD raw `status` / `readinessPhase` strings to `ForgeRuntimeStatus`.
 *
 * Rules:
 * - Unknown values → `"stopped"` (visible-but-not-healthy; never `destroyed`
 *   which would hide rows, never `running` which would show false green).
 * - Both `status` and `readinessPhase` are consulted; `status` wins when
 *   it carries a terminal signal (destroyed, error, draining, stopped).
 *
 * THE FALLBACK IS FOR STATUSES CMMD HAS NOT DECLARED, NOT FOR ONES WE FORGOT.
 * `status` crosses the boundary as a bare `z.string()` (CMMD's forge-context
 * `forgeRuntimeData` shares its shape with desktop runtimes, whose vocabulary
 * is `online`/`offline`), so nothing type-checks this mapper against CMMD's
 * enum and a missing entry is invisible — it just renders as "Suspended" with a
 * "Start project" button. `recycling` and `superseded` both sat in that hole.
 * The mirror of CMMD's `forgeRuntimeStatuses` lives in this module's test as
 * `CMMD_RUNTIME_STATUS_CONTRACT`; update it when CMMD's enum changes.
 */
/**
 * CMMD's raw status strings that decide the answer on their own, regardless of
 * readinessPhase. `running` is intentionally not a key — see the call site.
 */
const CMMD_TERMINAL_STATUS: Readonly<Record<string, ForgeRuntimeStatus>> = {
  destroyed: "destroyed",
  // A row CMMD replaced with a newer one. It is retired, not hibernated: there
  // is no machine here to start. CMMD's own `listByUser` excludes `superseded`
  // in the SAME filter as `destroyed`, so this mirrors the server's reading.
  // Absent from this table it fell to the unknown default (`stopped`), which
  // rendered as "Suspended" with a "Start project" button on a dead row.
  superseded: "destroyed",
  error: "error",
  failed: "error",
  draining: "draining",
  // CMMD is rotating the runtime's credentials / updating it in place, and
  // refuses connects until it finishes ("Forge runtime credential rotation is
  // in progress"). That is the same product situation as `draining`, and Forge
  // already says exactly the right thing for it — "Updating" in the sidebar,
  // "Project update in progress" in the route-blocked panel — so it maps onto
  // `draining` rather than adding a second status that renders identically.
  //
  // `recycling` is NOT filtered out of CMMD's `listByUser`, so it genuinely
  // reaches this mapper. Without this line it fell to `stopped`, which IS
  // auto-connectable-adjacent: the UI offered "Start project" on a runtime
  // mid-rotation and the click could only fail.
  recycling: "draining",
  stopped: "stopped",
  // NOT folded into `stopped`. See the union member's doc: a suspended sprite is
  // the resting state of a healthy runtime and resumes in under a second, so it
  // must stay distinguishable for the auto-connect decision.
  suspended: "suspended",
  provisioning: "provisioning",
  created: "provisioning",
  starting: "starting",
  restarting: "starting",
};

export function mapCmmdStatusToRuntimeStatus(
  status: string,
  readinessPhase?: string,
): ForgeRuntimeStatus {
  // Terminal/control-plane status values (authoritative regardless of phase).
  //
  // A lookup rather than a switch: every `case` is a branch, and the switch this
  // replaced crossed the repo's cyclomatic gate the moment `suspended` was added
  // (21, threshold 20). A table is cyclomatic 1 and stays flat however many
  // statuses CMMD grows, so the next status to be added cannot re-break it.
  //
  // `running` is deliberately ABSENT: it is the one status refined by
  // readinessPhase below, so a hit here would short-circuit that refinement.
  const terminal = CMMD_TERMINAL_STATUS[status];
  if (terminal !== undefined) return terminal;
  // Unknown status → stopped (safe visible default), and so is anything that is
  // not `running` — only `running` falls through to the readiness refinement.
  if (status !== "running") return "stopped";

  // status === "running" — consult readinessPhase for precision
  if (readinessPhase) {
    switch (readinessPhase) {
      case "app_ready":
      case "server_ready":
      case "ready":
      // `supervisor_up` is CMMD's TERMINAL healthy readinessPhase for a running
      // Sprite: the supervisor is up and the runtime is serving. CMMD does not
      // emit a later `app_ready`/`server_ready` at the runtime/catalog level
      // (that was a phantom enum); fine-grained app readiness is tracked
      // separately via the `/api/workspace/readiness` SSE (which reaches
      // `app-ready`). Treating `supervisor_up` as "starting" left freshly
      // provisioned cloud projects stuck on "Starting" forever, which blocked
      // new-thread creation. It is a running/ready runtime.
      case "supervisor_up":
        return "running";
      case "starting":
      case "image_pulling":
      case "waiting_state":
        return "starting";
      case "error":
        return "error";
      default:
        return "running";
    }
  }

  return "running";
}

// ── Filters ───────────────────────────────────────────────────────────────────

/** Returns true for runtimes that should be filtered out of the sidebar list. */
export function isDestroyedStatus(status: ForgeRuntimeStatus): boolean {
  return status === "destroyed";
}

/**
 * Whether a cloud runtime in this status should be AUTO-connected when its route
 * is opened. Only runtimes that are already up (or actively coming up) are
 * auto-connectable. A `stopped`/hibernated Machine must NOT auto-wake on open —
 * that silently starts a Sprite (cost) and blocks while it boots.
 * The user wakes it deliberately via "Start project". `error`/`destroyed`/
 * `draining` are also not auto-connectable (they need Restart, not a silent wake).
 */
export function isAutoConnectableRuntimeStatus(status: ForgeRuntimeStatus): boolean {
  switch (status) {
    case "running":
    case "starting":
    case "provisioning":
      return true;
    // Resuming a suspended sprite is NOT provisioning. The sprite already
    // exists; the next inbound request unfreezes it in well under a second
    // (0.646 s measured live 2026-08-04), so the ~400 s / real-cost argument
    // that keeps `registered` off this list does not apply. Without this,
    // opening a thread on an idle runtime — the normal case, since a sprite
    // suspends ~1 s after its last request — never connected, and the user got
    // a blank surface with no error.
    case "suspended":
      return true;
    // `registered` must NOT auto-connect: connecting would provision a sprite
    // (~400s and real cost) merely because the user opened a route. Provisioning
    // is a deliberate act — starting the first thread, or "Start project".
    case "registered":
    case "stopped":
    case "draining":
    case "error":
    case "destroyed":
      return false;
    default: {
      const _exhaustive: never = status;
      void _exhaustive;
      return false;
    }
  }
}

// ── Display name ──────────────────────────────────────────────────────────────

/**
 * Resolve a human-readable display name for a cloud project row.
 *
 * Priority:
 * 1. `projectName` (from CMMD `projects.title` INNER JOIN — preferred)
 * 2. basename of `githubRepoFullName` (e.g. "org/repo" → "repo")
 * 3. Terminal fallback: `"project <cmmdProjectId>"`
 *
 * A row is NEVER anonymous.
 */
export function resolveCloudProjectDisplayName(input: {
  /**
   * The project's Forge display name. Sits on TOP of the existing chain rather
   * than replacing it: a project with no display name must still reach the
   * repo-basename and `project N` fallbacks, or a cloud row can render nameless.
   */
  readonly shortName?: string | null;
  readonly projectName: string | null;
  readonly githubRepoFullName: string | null;
  readonly cmmdProjectId: number;
}): string {
  const short = input.shortName?.trim();
  if (short) {
    return short;
  }
  if (input.projectName) {
    return input.projectName;
  }
  if (input.githubRepoFullName) {
    const parts = input.githubRepoFullName.split("/");
    const basename = parts[parts.length - 1];
    if (basename) return basename;
  }
  return `project ${input.cmmdProjectId}`;
}
